<template>
  <div class="board-page">
    <div class="board-header">
      <div class="header-content">
        <h1>Ticket Board</h1>
        <p>Drag and drop tickets to update their status.</p>
      </div>
    </div>

    <div class="board-scroller">
      <div class="board">
        <div v-for="status in statuses" :key="status" class="column">
          <div class="column-header">
            <span :class="['status-dot', status.toLowerCase()]"></span>
            <h3>{{ status.replace('_', ' ') }}</h3>
            <span class="count-badge">{{ columns[status]?.length || 0 }}</span>
          </div>
          
          <draggable 
            v-model="columns[status]" 
            group="tickets" 
            @change="handleMove($event, status)"
            item-key="id"
            class="drag-area"
            ghost-class="ghost-card"
          >
            <template #item="{ element }">
              <div class="ticket-card" @click="router.push(`/ticket/${element.id}`)">
                <div class="card-tag" :class="(element.priority || 'MEDIUM').toLowerCase()"></div>
                <div class="card-body">
                  <div class="ticket-top">
                    <span class="ticket-id">TASK-{{ element.id }}</span>
                    <span :class="['priority-label', (element.priority || 'MEDIUM').toLowerCase()]">{{ element.priority }}</span>
                  </div>
                  <h4>{{ element.title }}</h4>
                  <div class="ticket-footer">
                    <div class="user-info">
                      <div class="avatar" :title="element.creator?.full_name">
                        {{ (element.creator?.full_name || 'U').charAt(0) }}
                      </div>
                      <span class="creator-name">{{ element.creator?.full_name || 'Unknown' }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </draggable>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive } from 'vue'
import { useRouter } from 'vue-router'
import draggable from 'vuedraggable'
import axios from 'axios'

const router = useRouter()
const statuses = ['NEW', 'ASSIGNED', 'IN_PROGRESS', 'PENDING_USER', 'RESOLVED', 'CLOSED'] as const
type Status = typeof statuses[number]

const columns = reactive<Record<Status, any[]>>({
  NEW: [],
  ASSIGNED: [],
  IN_PROGRESS: [],
  PENDING_USER: [],
  RESOLVED: [],
  CLOSED: []
})

const API_URL = (import.meta.env.VITE_API_URL || 'https://it-ticketing-api.maulanarofiq72.workers.dev').replace(/\/$/, '')

const fetchTickets = async () => {
  try {
    const response = await axios.get(`${API_URL}/tickets`)
    const tickets = response.data
    statuses.forEach(s => {
      columns[s] = tickets.filter((t: any) => t.status === s)
    })
  } catch (err) {
    console.error('Failed to fetch tickets', err)
  }
}

const handleMove = async (evt: any, newStatus: string) => {
  if (evt.added) {
    const ticket = evt.added.element
    try {
      await axios.put(`${API_URL}/tickets/${ticket.id}`, {
        status: newStatus
      })
    } catch (err) {
      console.error('Failed to update ticket status', err)
      fetchTickets() // Revert on failure
    }
  }
}

onMounted(fetchTickets)
</script>

<style scoped>
.board-page { padding: 2rem; background-color: #f4f5f7; height: calc(100vh - 64px); display: flex; flex-direction: column; }
.board-header { margin-bottom: 2rem; }
.header-content h1 { font-size: 1.8rem; color: #172b4d; margin-bottom: 0.25rem; }
.header-content p { color: #5e6c84; }
.board-scroller { flex: 1; overflow-x: auto; overflow-y: hidden; padding-bottom: 1rem; }
.board { display: flex; gap: 1.25rem; height: 100%; align-items: flex-start; }
.column { background-color: #f4f5f7; border: 1px solid #dfe1e6; border-radius: 8px; min-width: 320px; max-width: 320px; max-height: 100%; display: flex; flex-direction: column; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
.column-header { padding: 1rem; display: flex; align-items: center; gap: 0.75rem; border-bottom: 2px solid #dfe1e6; }
.status-dot { width: 10px; height: 10px; border-radius: 50%; }
.status-dot.new { background: #64748b; }
.status-dot.assigned { background: #0052cc; }
.status-dot.pending_user { background: #ff8b00; }
.status-dot.open { background: #0052cc; }
.status-dot.in_progress { background: #ff8b00; }
.status-dot.resolved { background: #36b37e; }
.status-dot.closed { background: #5e6c84; }
.column-header h3 { font-size: 0.85rem; font-weight: 700; color: #5e6c84; text-transform: uppercase; letter-spacing: 0.5px; margin: 0; flex: 1; }
.count-badge { background: #dfe1e6; color: #42526e; padding: 0.1rem 0.6rem; border-radius: 10px; font-size: 0.75rem; font-weight: 700; }
.drag-area { padding: 0.75rem; flex: 1; overflow-y: auto; min-height: 100px; }
.ticket-card { background: white; margin-bottom: 0.75rem; border-radius: 8px; box-shadow: 0 1px 3px rgba(9, 30, 66, 0.15); cursor: pointer; position: relative; overflow: hidden; transition: transform 0.15s, box-shadow 0.15s; }
.ticket-card:hover { transform: translateY(-2px); box-shadow: 0 4px 8px rgba(9, 30, 66, 0.12); }
.card-tag { position: absolute; top: 0; left: 0; width: 4px; height: 100%; }
.card-tag.critical { background: #de350b; }
.card-tag.high { background: #ff8b00; }
.card-tag.medium { background: #36b37e; }
.card-tag.low { background: #0052cc; }
.card-body { padding: 1rem; }
.ticket-top { display: flex; justify-content: space-between; margin-bottom: 0.75rem; }
.ticket-id { font-size: 0.75rem; font-weight: 700; color: #0052cc; }
.priority-label { font-size: 0.65rem; font-weight: 800; text-transform: uppercase; padding: 0.1rem 0.4rem; border-radius: 3px; }
.priority-label.critical { background: #ffebe6; color: #de350b; }
.priority-label.high { background: #fffae6; color: #ff8b00; }
.priority-label.medium { background: #e3fcef; color: #006644; }
.priority-label.low { background: #deebff; color: #0052cc; }
.card-body h4 { margin: 0 0 1rem 0; font-size: 0.95rem; color: #172b4d; line-height: 1.4; font-weight: 500; }
.ticket-footer { display: flex; justify-content: space-between; align-items: center; }
.user-info { display: flex; align-items: center; gap: 0.5rem; }
.avatar { width: 24px; height: 24px; background: #0747a6; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.7rem; font-weight: 700; }
.creator-name { font-size: 0.75rem; color: #5e6c84; }
.ghost-card { opacity: 0.5; background: #ebecf0 !important; border: 2px dashed #97a0af !important; box-shadow: none !important; }
</style>
